import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  LogOut,
  X,
  AlertTriangle,
  Route,
  MapPin,
  Clock,
  Tag,
  Info,
} from "lucide-react";
import BottomNavigation from "@/components/BottomNavigation";
import { ShipmentCard } from "@/components/ShipmentCard";
import { useIdentity } from "@/auth/identity";
import { useState, useEffect } from "react";
import { tripService } from "@/services/api";
import { clearUserScopeFromStorage } from "@/utils/scopeStorage";
import { toShipmentInfos } from "@/utils/shipmentInfos";
import { shiftBanners, ShiftBanner } from "@/utils/shiftBanners";
import { useDriver } from "@/context/DriverContext";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { toast } from "@/hooks/use-toast";
import { getArgentinaDate, getArgentinaTomorrowDate, formatDate } from "@/utils/dateUtils";
import { getDriverDisplayName } from "@/utils/driverDisplayName";
import { ShipmentInfo, Stop } from "@/types";
import { useShiftDisplay } from "@/hooks/useShiftDisplay";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { clearLocalStorageKeepingIdp } from "@/auth/idpSession";

// Cartel por turno cuando el chofer declaró disponibilidad y no tiene ruta en
// ese turno: "no te citaron" (naranja) o "seguís disponible" (gris).
const ShiftBannerCard = ({
  banner,
  dayLabel,
}: {
  banner: ShiftBanner;
  dayLabel: "hoy" | "mañana";
}) => {
  const shiftDisplay = useShiftDisplay();
  const turno = banner.shift ? (
    <>
      {" "}
      (turno{" "}
      <span translate="no">
        {shiftDisplay.inSentence(banner.shift, banner.operationIds)}
      </span>
      )
    </>
  ) : null;
  const notAssigned = banner.kind === "NOT_ASSIGNED";
  return (
    <div
      className={cn(
        "w-full p-4 rounded-lg border",
        notAssigned
          ? "bg-orange-100 text-orange-800 border-orange-300"
          : "bg-gray-100 text-gray-600 border-gray-300"
      )}
    >
      <div className="text-center">
        <div className="text-sm font-bold mb-1">
          {notAssigned ? (
            <>No se te asignó ruta para {dayLabel}{turno}</>
          ) : (
            <>Seguís disponible para {dayLabel}{turno}</>
          )}
        </div>
        <div className="text-xs">
          {notAssigned
            ? "No se te asignó ruta en esta planificación, pero seguís disponible"
            : "Todavía no hay planificación para este turno"}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useIdentity();
  const navigate = useNavigate();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [hasTriedLoadToday, setHasTriedLoadToday] = useState(false);

  // Estados para el modal de cancelación
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelShipmentId, setCancelShipmentId] = useState<string | null>(null);
  const [cancelShipmentData, setCancelShipmentData] = useState<ShipmentInfo | null>(null);
  const [cancelComment, setCancelComment] = useState("");
  const [showPenaltyAlert, setShowPenaltyAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados para el modal de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmShipmentId, setConfirmShipmentId] = useState<string | null>(
    null
  );
  const [confirmShipmentData, setConfirmShipmentData] = useState<any>(null);

  // Estados para el modal de paradas
  const [stopsModalOpen, setStopsModalOpen] = useState(false);
  const [selectedStops, setSelectedStops] = useState<Stop[] | null>(null);

  // Helper functions for SVC colors
  const getSvcColor = (svcCode: string) => {
    if (!svcCode) return "bg-gray-100 text-gray-800";
    let hash = 0;
    for (let i = 0; i < svcCode.length; i++) {
      hash = svcCode.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
      "bg-primary-soft text-primary-soft-foreground",
      "bg-purple-100 text-purple-800",
      "bg-green-100 text-green-800",
      "bg-yellow-100 text-yellow-800",
      "bg-pink-100 text-pink-800",
      "bg-indigo-100 text-indigo-800",
      "bg-orange-100 text-orange-800",
      "bg-teal-100 text-teal-800",
      "bg-cyan-100 text-cyan-800",
      "bg-lime-100 text-lime-800",
      "bg-red-100 text-red-800",
      "bg-emerald-100 text-emerald-800",
      "bg-violet-100 text-violet-800",
      "bg-amber-100 text-amber-800",
      "bg-rose-100 text-rose-800",
    ];
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getSvcBorderColor = (svcId: number | null) => {
    if (!svcId) return "border-l-primary";
    const svcColor = getSvcColor(`svc-${svcId}`);
    if (svcColor.includes("primary")) return "border-l-primary";
    if (svcColor.includes("purple")) return "border-l-purple-500";
    if (svcColor.includes("green")) return "border-l-green-500";
    if (svcColor.includes("yellow")) return "border-l-yellow-500";
    if (svcColor.includes("pink")) return "border-l-pink-500";
    if (svcColor.includes("indigo")) return "border-l-indigo-500";
    if (svcColor.includes("orange")) return "border-l-orange-500";
    if (svcColor.includes("teal")) return "border-l-teal-500";
    if (svcColor.includes("cyan")) return "border-l-cyan-500";
    if (svcColor.includes("lime")) return "border-l-lime-500";
    if (svcColor.includes("red")) return "border-l-red-500";
    if (svcColor.includes("emerald")) return "border-l-emerald-500";
    if (svcColor.includes("violet")) return "border-l-violet-500";
    if (svcColor.includes("amber")) return "border-l-amber-500";
    if (svcColor.includes("rose")) return "border-l-rose-500";
    return "border-l-primary";
  };

  // Handler para abrir modal de paradas
  const handleOpenStopsModal = (stops: Stop[] | null) => {
    if (stops && stops.length > 0) {
      setSelectedStops(stops);
      setStopsModalOpen(true);
    }
  };

  const {
    userScope,
    driverProfile,
    clearDriverProfile,
    todayShipments,
    setTodayShipments,
    tomorrowShipments,
    setTomorrowShipments,
    updateShipmentStatus,
    setLastFetchDate,
    clearLastFetchDate,
    isDataValid,
    isAvailable,
    setIsAvailable,
    isApiClientReady,
    clearUserScope,
    clearIsAvailable,
    setTodayCoordinations,
    setTomorrowCoordinations,
    clearTodayCoordinations,
    clearTomorrowCoordinations,
  } = useDriver();
  const shiftDisplay = useShiftDisplay();

  // Función para refrescar disponibilidad (puede ser llamada desde otras páginas)
  const refreshAvailability = async () => {
    setForceRefresh(true);
  };

  // Función para abrir el modal de cancelación
  const handleCancelTrip = (shipment: ShipmentInfo) => {
    setCancelShipmentId(shipment.id);
    setCancelShipmentData(shipment);
    setCancelComment("");
    setShowPenaltyAlert(false);
    setShowCancelModal(true);
  };

  // Función para cerrar el modal de cancelación
  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setCancelShipmentId(null);
    setCancelShipmentData(null);
    setCancelComment("");
    setShowPenaltyAlert(false);
  };

  // Función para abrir el modal de confirmación
  const handleConfirmTrip = (shipmentId: string, shipmentData: any) => {
    setConfirmShipmentId(shipmentId);
    setConfirmShipmentData(shipmentData);
    setShowConfirmModal(true);
  };

  // Función para cerrar el modal de confirmación
  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setConfirmShipmentId(null);
    setConfirmShipmentData(null);
  };

  // Iniciar / finalizar llevan el id de la tarjeta que los lanzó
  const handleStartTrip = (shipment: ShipmentInfo) =>
    navigate("/start-trip", { state: { shipmentId: shipment.id } });
  const handleEndTrip = (shipment: ShipmentInfo) =>
    navigate("/end-trip", { state: { shipmentId: shipment.id } });

  // Recarga las rutas de hoy y mañana (tras confirmar / cancelar)
  const reloadShipments = async () => {
    try {
      const todayResponse = await tripService.getTodayShipment();
      setTodayShipments(toShipmentInfos(todayResponse.data?.data ?? []));

      const tomorrowResponse = await tripService.getTomorrowShipment();
      setTomorrowShipments(toShipmentInfos(tomorrowResponse.data?.data ?? []));
    } catch (error) {
      console.error("Error recargando datos:", error);
    }
  };

  // Función para confirmar la cancelación
  const handleConfirmCancel = async () => {
    if (!cancelShipmentId) return;

    if (!showPenaltyAlert) {
      // Mostrar alerta de multa económica
      setShowPenaltyAlert(true);
      return;
    }

    setIsSubmitting(true);

    try {
      await tripService.endShipment(cancelShipmentId, {
        status: "CANCELADO",
        comment: cancelComment,
      });

      // Actualizar el estado de ESA ruta en el contexto
      updateShipmentStatus(cancelShipmentId, "CANCELADO");

      toast({
        title: "Viaje cancelado",
        description: "El viaje ha sido cancelado correctamente",
      });

      // Refrescar los datos del dashboard
      setForceRefresh(true);

      // Recargar los datos de hoy y mañana
      await reloadShipments();
      handleCloseCancelModal();
    } catch (error) {
      console.error("Error cancelando viaje:", error);
      toast({
        title: "Error",
        description: "No se pudo cancelar el viaje",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para confirmar la disponibilidad
  const handleConfirmAvailability = async () => {
    if (!confirmShipmentId) return;

    setIsSubmitting(true);

    try {
      await tripService.endShipment(confirmShipmentId, {
        status: "CONFIRMADO",
      });

      // Actualizar el estado de ESA ruta en el contexto
      updateShipmentStatus(confirmShipmentId, "CONFIRMADO");

      toast({
        title: "Disponibilidad confirmada",
        description: "Has confirmado tu disponibilidad para este viaje",
      });

      // Refrescar los datos del dashboard
      setForceRefresh(true);

      // Recargar los datos de hoy y mañana
      await reloadShipments();
      handleCloseConfirmModal();
    } catch (error) {
      console.error("Error confirmando disponibilidad:", error);
      toast({
        title: "Error",
        description: "No se pudo confirmar la disponibilidad",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Efecto simplificado para cargar datos de disponibilidad
  useEffect(() => {
    const loadDriverData = async () => {
      try {
        const response = await tripService.getDriverAvailability();
        setIsAvailable(response.data?.data?.available || false);

        // Cache simple: solo guardar fecha en zona horaria de Argentina
        const today = getArgentinaDate();
        setLastFetchDate(today);

        setForceRefresh(false);
      } catch (error) {
        // Solo actualizar lastFetchDate si es un error 404 (usuario sin availability)
        // Para otros errores (conexión, 500, etc.) mantener el retry
        if (error.response?.status === 404) {
          setIsAvailable(false);
          const today = getArgentinaDate();
          setLastFetchDate(today);
        } else {
          // Para errores de conexión, no actualizar lastFetchDate para permitir retry
          setIsAvailable(null);
        }

        setForceRefresh(false);
      }
    };

    // Lógica simple: cargar si no hay datos o si es un nuevo día
    const shouldLoad = !isDataValid() || forceRefresh;

    if (user?.sub && shouldLoad && isApiClientReady) {
      loadDriverData();
      // Resetear los flags cuando se carga por un nuevo día
      if (!isDataValid()) {
        setHasTriedLoadToday(false);
      }
    }
  }, [
    user?.sub,
    forceRefresh,
    isApiClientReady,
    isDataValid,
    setLastFetchDate,
    setIsAvailable,
  ]);

  // Carteles por turno (no te citaron / seguís disponible) para hoy y mañana
  const [todayBanners, setTodayBanners] = useState<ShiftBanner[]>([]);
  const [tomorrowBanners, setTomorrowBanners] = useState<ShiftBanner[]>([]);

  // Single loading state for entire dashboard
  const [isLoading, setIsLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);

  // Efecto unificado para cargar todos los datos del dashboard
  useEffect(() => {
    const loadAllData = async () => {
      if (!user?.sub || !isApiClientReady || hasTriedLoadToday) return;

      setIsLoading(true);
      setDataReady(false);
      setHasTriedLoadToday(true);

      try {
        // Cargar todo en paralelo
        const todayStr = getArgentinaDate();
        const tomorrowStr = getArgentinaTomorrowDate();

        const [
          shipmentResponse,
          availabilityResponse,
          tomorrowShipmentResponse,
          todayCoordResponse,
          tomorrowCoordResponse,
        ] = await Promise.all([
          tripService.getTodayShipment(),
          tripService.getDriverAvailability().catch(() => null),
          tripService
            .getTomorrowShipment()
            .catch(() => ({ data: { data: [] } })),
          tripService.getMyCoordinations(todayStr).catch(() => ({ data: { data: [] } })),
          tripService.getMyCoordinations(tomorrowStr).catch(() => ({ data: { data: [] } })),
        ]);

        const shipments = shipmentResponse.data?.data;
        const availabilities = availabilityResponse?.data?.data;
        const tomorrowRows = tomorrowShipmentResponse.data?.data;

        // Una tarjeta por ruta: todas las de hoy y todas las de mañana
        const todayList = toShipmentInfos(shipments ?? []);
        const tomorrowList = toShipmentInfos(tomorrowRows ?? []);
        setTodayShipments(todayList);
        setTomorrowShipments(tomorrowList);

        // Carteles por turno: disponibilidades del día sin ruta en ese turno.
        // Se filtra por el string de fecha de Argentina (no por día UTC):
        // entre las 21 y las 24hs ART, UTC ya es el día siguiente, y comparar
        // por día UTC mezclaba los carteles de "hoy" con filas de mañana.
        const avList: {
          date: string;
          shift: string | null;
          assignedToday: boolean | null;
          svc?: { operationId?: number | null } | null;
        }[] = Array.isArray(availabilities) ? availabilities : [];

        setTodayBanners(
          shiftBanners(
            avList.filter(
              (av) => typeof av.date === "string" && av.date.slice(0, 10) === todayStr
            ),
            todayList
          )
        );
        setTomorrowBanners(
          shiftBanners(
            avList.filter(
              (av) => typeof av.date === "string" && av.date.slice(0, 10) === tomorrowStr
            ),
            tomorrowList
          )
        );

        const todayCoordData = todayCoordResponse.data?.data;
        setTodayCoordinations(Array.isArray(todayCoordData) ? todayCoordData : null);

        const tomorrowCoordData = tomorrowCoordResponse.data?.data;
        setTomorrowCoordinations(Array.isArray(tomorrowCoordData) ? tomorrowCoordData : null);
      } catch (error) {
        console.error("Error obteniendo shipments:", error);
        setTodayShipments([]);
        setTomorrowShipments([]);
        setTodayBanners([]);
        setTomorrowBanners([]);
        clearTodayCoordinations();
        clearTomorrowCoordinations();
      } finally {
        setIsLoading(false);
        // Pequeño delay antes de mostrar el contenido para transición suave
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setDataReady(true);
          });
        });
      }
    };

    loadAllData();
  }, [
    user?.sub,
    isApiClientReady,
    hasTriedLoadToday,
    setTodayShipments,
    setTomorrowShipments,
  ]);

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex justify-between items-center max-w-full">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold truncate">Choferes</h1>
          </div>
          <div className="flex items-center space-x-3">
            <p className="text-sm opacity-90 truncate">
              {getDriverDisplayName(driverProfile, user?.name, user?.email)}
            </p>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                const sub = user?.sub ?? undefined;
                clearUserScopeFromStorage(sub);
                clearUserScope(sub);
                setTodayShipments([]);
                setTomorrowShipments([]);
                clearLastFetchDate();
                clearIsAvailable();
                clearDriverProfile();
                clearLocalStorageKeepingIdp();
                logout({ logoutParams: { returnTo: window.location.origin } });
              }}
              className="text-white flex-shrink-0"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="p-4 max-w-full">
        {/* Loading State - Skeleton completo */}
        {isLoading && (
          <div className="space-y-4 mt-6">
            {/* Skeleton para botón de acción */}
            <Skeleton className="w-full h-20 rounded-lg" />

            {/* Skeleton para ruta de hoy */}
            <div className="w-full p-4 rounded-lg border bg-white border-gray-200">
              <div className="space-y-3">
                <Skeleton className="h-5 w-2/3 mx-auto" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
                <div className="flex justify-center gap-2 pt-2">
                  <Skeleton className="h-8 w-24 rounded-md" />
                  <Skeleton className="h-8 w-24 rounded-md" />
                </div>
              </div>
            </div>

            {/* Skeleton para ruta de mañana */}
            <div className="w-full p-4 rounded-lg border bg-white border-gray-200">
              <div className="space-y-3">
                <Skeleton className="h-5 w-2/3 mx-auto" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              </div>
            </div>

            {/* Skeleton para botón de disponibilidad */}
            <Skeleton className="w-full h-20 rounded-lg" />
          </div>
        )}

        {/* Contenido real - aparece todo junto con transición */}
        {!isLoading && (
          <div
            className={cn(
              "space-y-4 mt-6 transition-all duration-500 ease-out",
              dataReady
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            )}
          >
            {/* Rutas de hoy: una tarjeta por ruta, con sus propias acciones */}
            {todayShipments.map((s) => (
              <ShipmentCard
                key={s.id}
                shipment={s}
                dayLabel="Hoy"
                onConfirm={(sh) => handleConfirmTrip(sh.id, sh)}
                onCancel={handleCancelTrip}
                onStart={handleStartTrip}
                onEnd={handleEndTrip}
                onOpenStops={handleOpenStopsModal}
              />
            ))}

            {todayBanners.map((b) => (
              <ShiftBannerCard key={`hoy-${b.shift}`} banner={b} dayLabel="hoy" />
            ))}

            {hasTriedLoadToday &&
              todayShipments.length === 0 &&
              todayBanners.length === 0 && (
                <div className="w-full p-4 rounded-lg border bg-gray-100 text-gray-600 border-gray-300">
                  <div className="text-center">
                    <div className="text-sm font-bold mb-1">
                      No hay viajes para hoy
                    </div>
                    <div className="text-xs">
                      No tienes viajes asignados para el día de hoy
                    </div>
                  </div>
                </div>
              )}

            {/* Rutas de mañana */}
            {tomorrowShipments.map((s) => (
              <ShipmentCard
                key={s.id}
                shipment={s}
                dayLabel="Mañana"
                onConfirm={(sh) => handleConfirmTrip(sh.id, sh)}
                onCancel={handleCancelTrip}
                onStart={handleStartTrip}
                onEnd={handleEndTrip}
                onOpenStops={handleOpenStopsModal}
              />
            ))}

            {tomorrowBanners.map((b) => (
              <ShiftBannerCard key={`manana-${b.shift}`} banner={b} dayLabel="mañana" />
            ))}

            {hasTriedLoadToday &&
              tomorrowShipments.length === 0 &&
              tomorrowBanners.length === 0 && (
                <div className="w-full p-4 rounded-lg border bg-gray-100 text-gray-600 border-gray-300">
                  <div className="text-center">
                    <div className="text-sm font-bold mb-1">
                      No hay rutas para mañana
                    </div>
                    <div className="text-xs">
                      No tienes rutas asignadas para mañana
                    </div>
                  </div>
                </div>
              )}

            <Button
              className="w-full h-20 text-base justify-start px-4 flex items-center"
              variant="outline"
              onClick={() => navigate("/report-availability")}
            >
              <Calendar className="mr-3 h-5 w-5 flex-shrink-0" />
              <div className="text-left flex-1 min-w-0">
                <span className="block text-base font-medium">
                  Informar disponibilidad
                </span>
                <span className="block text-sm opacity-90">
                  Establece tu disponibilidad
                </span>
              </div>
            </Button>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation />

      {/* Modal de Cancelación */}
      {showCancelModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseCancelModal}
        >
          <div
            className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-gray-900">
                  Cancelar Viaje
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCloseCancelModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {cancelShipmentData && (
                <p className="text-sm text-gray-600 mb-4">
                  {cancelShipmentData.svc} · Turno{" "}
                  <span translate="no">
                    {shiftDisplay.inSentence(cancelShipmentData.shift, [cancelShipmentData.operationId])}
                  </span>
                </p>
              )}

              {!showPenaltyAlert ? (
                <div className="space-y-4">
                  <Alert className="border-red-200 bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-800">
                      ¿Estás seguro de cancelar este viaje?
                    </AlertTitle>
                    <p className="text-sm text-red-700 mt-1">
                      Esta acción no se puede deshacer.
                    </p>
                  </Alert>

                  <div>
                    <Label
                      htmlFor="cancelComment"
                      className="block mb-2 font-medium"
                    >
                      Motivo de la cancelación (opcional)
                    </Label>
                    <Textarea
                      id="cancelComment"
                      placeholder="Describe el motivo de la cancelación..."
                      value={cancelComment}
                      onChange={(e) => setCancelComment(e.target.value)}
                      className="w-full"
                      rows={3}
                    />
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      variant="outline"
                      onClick={handleCloseCancelModal}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleConfirmCancel}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      Confirmar Cancelación
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Alert className="border-red-200 bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-800">¡ATENCIÓN!</AlertTitle>
                    <p className="text-sm text-red-700 mt-1 font-bold">
                      Estás cancelando un viaje ya asignado, serás multado
                      económicamente.
                    </p>
                  </Alert>

                  <div className="flex space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowPenaltyAlert(false)}
                      className="flex-1"
                    >
                      Volver
                    </Button>
                    <Button
                      onClick={handleConfirmCancel}
                      disabled={isSubmitting}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      {isSubmitting ? "Procesando..." : "Confirmar y Multar"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación */}
      {showConfirmModal && confirmShipmentData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseConfirmModal}
        >
          <div
            className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-gray-900">
                  Confirmar Disponibilidad
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCloseConfirmModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <Alert className="border-green-200 bg-green-50">
                  <AlertTriangle className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">
                    ¿Confirmas tu disponibilidad?
                  </AlertTitle>
                  <p className="text-sm text-green-700 mt-1">
                    Al confirmar, aceptas realizar este viaje asignado.
                  </p>
                </Alert>

                {/* Información del shipment */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">
                    Detalles del Viaje
                  </h3>
                  <div className="text-sm space-y-1">
                    <div>
                      <span className="font-medium">Zona:</span>{" "}
                      {confirmShipmentData.svc}
                    </div>
                    <div>
                      <span className="font-medium">Turno:</span>{" "}
                      <span translate="no">{shiftDisplay.forOperations(confirmShipmentData.shift, [confirmShipmentData.operationId])}</span>
                    </div>
                    <div>
                      <span className="font-medium">Hora:</span>{" "}
                      {confirmShipmentData.time}
                    </div>
                    <div>
                      <span className="font-medium">Dirección:</span>{" "}
                      {confirmShipmentData.address}
                    </div>
                    <div>
                      <span className="font-medium">Fecha:</span>{" "}
                      {formatDate(confirmShipmentData.date)}
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={handleCloseConfirmModal}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleConfirmAvailability}
                    disabled={isSubmitting}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {isSubmitting
                      ? "Procesando..."
                      : "Confirmar Disponibilidad"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Paradas */}
      <Dialog open={stopsModalOpen} onOpenChange={setStopsModalOpen}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Route className="h-5 w-5 text-primary" />
              Paradas de la Ruta
            </DialogTitle>
            <DialogDescription className="text-sm">
              {selectedStops && selectedStops.length > 0
                ? `${selectedStops.length} parada${
                    selectedStops.length > 1 ? "s" : ""
                  } en esta ruta`
                : "No hay paradas disponibles"}
            </DialogDescription>
          </DialogHeader>
          {selectedStops &&
            selectedStops.length > 0 &&
            (() => {
              // Calculate if there are multiple SVCs
              const uniqueSvcIds = new Set(
                selectedStops
                  .map((stop) => stop.svcId)
                  .filter((id): id is number => id !== null && id !== undefined)
              );
              const hasMultipleSvcs = uniqueSvcIds.size > 1;

              // Group stops by sub-route
              const stopsBySubRoute = selectedStops.reduce((acc, stop) => {
                const subRouteKey = stop.routeSubId || "Sin sub-ruta";
                if (!acc[subRouteKey]) {
                  acc[subRouteKey] = [];
                }
                acc[subRouteKey].push(stop);
                return acc;
              }, {} as Record<string, typeof selectedStops>);

              // Format time helper
              const formatTime = (time: string | null) => {
                if (!time) return null;
                return time.substring(0, 5);
              };

              return (
                <div className="space-y-4 py-2">
                  {/* Multiple SVCs Warning */}
                  {hasMultipleSvcs && (
                    <div className="w-full flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-800">
                        Esta ruta contiene paradas con diferentes SVC. Se
                        mostrarán con colores diferentes.
                      </p>
                    </div>
                  )}

                  {/* Stops Grouped by Sub-Route */}
                  <div className="space-y-6">
                    {Object.entries(stopsBySubRoute).map(
                      ([subRouteKey, stops]) => (
                        <div key={subRouteKey} className="flex gap-3 sm:gap-4">
                          {/* Vertical Sub-Route Label */}
                          <div className="flex-shrink-0">
                            <div className="sticky top-4">
                              <div className="bg-purple-100 border border-purple-200 rounded-lg p-2 sm:p-3 min-h-[60px] flex items-center justify-center">
                                <div className="flex flex-col items-center gap-1.5">
                                  <Route className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-700" />
                                  <div className="text-lg sm:text-xl font-bold text-purple-800">
                                    {subRouteKey}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Stops List */}
                          <div className="flex-1 space-y-2.5">
                            {stops.map((stop) => {
                              // Build full address
                              const addressParts = [
                                stop.address,
                                stop.number,
                                stop.neighborhood,
                                stop.city,
                                stop.province,
                                stop.postalCode,
                              ].filter(Boolean);
                              const fullAddress = addressParts.join(", ");

                              const timeRange =
                                stop.startTime || stop.endTime
                                  ? `${formatTime(stop.startTime) || "—"} - ${
                                      formatTime(stop.endTime) || "—"
                                    }`
                                  : null;

                              // Get SVC color
                              const svcColor = stop.svcId
                                ? getSvcColor(`svc-${stop.svcId}`)
                                : "bg-gray-100 text-gray-800";

                              return (
                                <div
                                  key={stop.id}
                                  className={cn(
                                    "group relative border rounded-lg bg-white transition-all duration-200",
                                    "shadow-sm",
                                    stop.svcId
                                      ? `border-l-4 ${getSvcBorderColor(
                                          stop.svcId
                                        )}`
                                      : "border-l-4 border-l-gray-300"
                                  )}
                                >
                                  <div className="p-3 sm:p-4">
                                    {/* Header: Stop Number, Name, and Badges */}
                                    <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                                      <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary-soft text-primary-soft-foreground font-semibold text-xs sm:text-sm flex-shrink-0 ring-2 ring-primary/30">
                                        {stop.stopNumber}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-semibold text-sm sm:text-base text-gray-900 mb-1 sm:mb-1.5 line-clamp-1">
                                          {stop.name ||
                                            `Parada ${stop.stopNumber}`}
                                        </h4>
                                        {stop.svcId && (
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span
                                              className={cn(
                                                "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border",
                                                svcColor,
                                                "border-current/20"
                                              )}
                                            >
                                              <Tag className="h-3 w-3" />
                                              SVC: {stop.svcId}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Details Grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                      {/* Address */}
                                      {fullAddress && (
                                        <div className="sm:col-span-2 flex items-start gap-2">
                                          <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-500 mb-0.5">
                                              Dirección
                                            </p>
                                            <p className="text-sm text-gray-900 line-clamp-2">
                                              {fullAddress}
                                            </p>
                                          </div>
                                        </div>
                                      )}

                                      {/* Time Range */}
                                      {timeRange && (
                                        <div className="flex items-start gap-2">
                                          <Clock className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-500 mb-0.5">
                                              Horario
                                            </p>
                                            <p className="text-sm text-gray-900 font-mono">
                                              {timeRange}
                                            </p>
                                          </div>
                                        </div>
                                      )}

                                      {/* Additional Info */}
                                      {stop.additionalInfo && (
                                        <div className="sm:col-span-2 flex items-start gap-2">
                                          <Info className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-500 mb-0.5">
                                              Información Adicional
                                            </p>
                                            <p className="text-sm text-gray-900">
                                              {stop.additionalInfo}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            })()}
          <DialogFooter>
            <Button
              onClick={() => setStopsModalOpen(false)}
              variant="outline"
              className="w-full sm:w-auto"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
